import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ArrowLeft, MapPin, Clock, Mountain, DollarSign, Calendar, Shield, Users } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const DIFF_COLORS = {
  "Easy": "bg-field/20 text-field",
  "Moderate": "bg-brass/20 text-brass",
  "Challenging": "bg-copper/20 text-copper",
  "Extreme": "bg-copper/30 text-copper",
};

export default function ExpeditionDetail() {
  const { slug } = useParams();
  const [species, setSpecies] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/packages/by-species/${slug}`)
      .then((r) => {
        setSpecies(r.data.species);
        setPackages(r.data.packages);
        setLoading(false);
      })
      .catch(() => { setError("Expedition not found"); setLoading(false); });
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-bone flex items-center justify-center pt-20"><p className="font-body text-slate">Loading expedition...</p></div>;
  if (error || !species) return (
    <div className="min-h-screen bg-bone flex flex-col items-center justify-center pt-20 gap-4">
      <p className="font-body text-slate">{error || "Not found"}</p>
      <Link to="/expeditions" className="btn-outline-dark">Back to Expeditions</Link>
    </div>
  );

  const heroImg = packages[0]?.continent === "Africa"
    ? "https://dispatchhunts.com/wp-content/uploads/2025/03/2b3bd60b-3821-40e2-b178-75f16c50775e.jpeg"
    : packages[0]?.country_code === "US" && packages[0]?.region?.includes("Alaska")
    ? "https://dispatchhunts.com/wp-content/uploads/2024/12/Alaskan-fall-brown-bear-hunt.jpg"
    : "https://dispatchhunts.com/wp-content/uploads/2024/12/Tajikistan-Marco-Polo.jpg";

  return (
    <>
      <Helmet>
        <title>{`${species.name} Expeditions | Dispatch Hunts`}</title>
        <meta name="description" content={`${species.name} hunting and fishing expeditions. ${species.description}. Starting at $${species.price_low?.toLocaleString()}.`} />
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-end" data-testid="expedition-detail-hero">
        <div className="absolute inset-0">
          <img src={heroImg} alt={species.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-carbon via-carbon/60 to-transparent" />
        </div>
        <div className="container-dh relative z-10 pb-12 pt-32">
          <Link to="/expeditions" className="inline-flex items-center gap-2 text-bone/60 hover:text-brass font-label text-xs tracking-wider uppercase mb-4 transition-colors" data-testid="back-to-expeditions">
            <ArrowLeft className="w-4 h-4" /> Back to Expeditions
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <span className="text-label text-brass text-[10px] tracking-wider uppercase">{species.category}</span>
            <span className={`text-label text-[10px] tracking-wider uppercase px-2 py-0.5 ${DIFF_COLORS[species.difficulty] || "bg-smoke/20 text-smoke"}`}>{species.difficulty}</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-bone" data-testid="expedition-detail-title">{species.name}</h1>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="bg-carbon/95 py-6" data-testid="expedition-stats">
        <div className="container-dh grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="flex items-center gap-3">
            <DollarSign className="w-5 h-5 text-brass" />
            <div>
              <p className="font-mono text-lg text-bone font-bold">${species.price_low?.toLocaleString()} – ${species.price_high?.toLocaleString()}</p>
              <p className="text-label text-bone/40 text-[10px]">Price Range</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-brass" />
            <div>
              <p className="font-body text-bone text-sm">{species.season}</p>
              <p className="text-label text-bone/40 text-[10px]">Season</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-brass" />
            <div>
              <p className="font-body text-bone text-sm">{species.countries?.length} {species.countries?.length === 1 ? "country" : "countries"}</p>
              <p className="text-label text-bone/40 text-[10px]">Available In</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Mountain className="w-5 h-5 text-brass" />
            <div>
              <p className="font-body text-bone text-sm">{species.difficulty}</p>
              <p className="text-label text-bone/40 text-[10px]">Difficulty</p>
            </div>
          </div>
        </div>
      </section>

      {/* Description + Packages */}
      <section className="section-light py-16 lg:py-24" data-testid="expedition-packages">
        <div className="container-dh">
          {species.description && (
            <div className="max-w-3xl mb-14">
              <p className="font-editorial text-carbon text-xl leading-relaxed">{species.description}</p>
            </div>
          )}

          <p className="text-label text-brass mb-3">{packages.length} Available {packages.length === 1 ? "Package" : "Packages"}</p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-carbon mb-10">Book your {species.name.toLowerCase()} expedition</h2>

          <div className="space-y-6">
            {packages.map((pkg) => (
              <div key={pkg.sku} className="bg-chalk border border-carbon/10 hover:border-brass/30 transition-all" data-testid={`package-card-${pkg.sku}`}>
                <div className="p-6 lg:p-8">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
                    <div>
                      <p className="text-label text-brass text-[10px] tracking-wider uppercase mb-1">{pkg.country} — {pkg.region}</p>
                      <h3 className="font-display text-xl font-bold text-carbon">{pkg.trip_type}</h3>
                      <p className="font-body text-sm text-slate mt-1">{pkg.subcategory}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-2xl font-bold text-carbon">${pkg.price_low?.toLocaleString()} – ${pkg.price_high?.toLocaleString()}</p>
                      <p className="text-label text-smoke text-[10px]">{pkg.price_notes || "USD"} • {pkg.price_tier}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="flex items-start gap-3">
                      <Clock className="w-4 h-4 text-brass mt-0.5" />
                      <div>
                        <p className="font-label text-[10px] tracking-wider uppercase text-smoke">Duration</p>
                        <p className="font-body text-sm text-carbon">{pkg.duration} days</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Calendar className="w-4 h-4 text-brass mt-0.5" />
                      <div>
                        <p className="font-label text-[10px] tracking-wider uppercase text-smoke">Season</p>
                        <p className="font-body text-sm text-carbon">{pkg.season_notes || `${pkg.season_start} – ${pkg.season_end}`}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Users className="w-4 h-4 text-brass mt-0.5" />
                      <div>
                        <p className="font-label text-[10px] tracking-wider uppercase text-smoke">Group Size</p>
                        <p className="font-body text-sm text-carbon">{pkg.group_min}–{pkg.group_max} hunters</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <p className="font-label text-[10px] tracking-wider uppercase text-brass mb-2">What's Included</p>
                      <p className="font-body text-sm text-slate leading-relaxed">{pkg.whats_included}</p>
                    </div>
                    <div>
                      <p className="font-label text-[10px] tracking-wider uppercase text-brass mb-2">Target Species</p>
                      <p className="font-body text-sm text-slate leading-relaxed">{pkg.target_species}</p>
                    </div>
                  </div>

                  {pkg.highlights && (
                    <div className="bg-brass/5 border-l-2 border-brass/40 p-4 mb-6">
                      <p className="font-body text-sm text-carbon leading-relaxed">{pkg.highlights}</p>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-carbon/10">
                    <div className="flex items-center gap-4">
                      <span className={`text-label text-[10px] tracking-wider uppercase px-2 py-1 ${DIFF_COLORS[pkg.difficulty] || "bg-smoke/20 text-smoke"}`}>
                        <Shield className="w-3 h-3 inline mr-1" />{pkg.difficulty}
                      </span>
                      {pkg.permits && <span className="font-body text-xs text-smoke">{pkg.permits}</span>}
                    </div>
                    <Link to="/apply" className="btn-brass !text-xs" data-testid={`apply-for-${pkg.sku}`}>
                      Apply for This Hunt <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {packages.length === 0 && (
            <div className="text-center py-16 bg-chalk border border-carbon/10">
              <p className="font-display text-xl text-carbon mb-4">No packages listed yet for {species.name}</p>
              <p className="font-body text-slate mb-6">We still coordinate {species.name.toLowerCase()} expeditions. Apply below and we'll build a custom trip.</p>
              <Link to="/apply" className="btn-brass">Apply for a Consultation <ArrowRight className="w-4 h-4" /></Link>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-dark grain py-16" data-testid="expedition-cta">
        <div className="container-dh text-center max-w-2xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-bone mb-4">Ready to hunt {species.name.toLowerCase()}?</h2>
          <p className="font-body text-bone/50 mb-8">Tell us what you're after. We'll match you with the right outfitter and handle every detail.</p>
          <Link to="/apply" className="btn-brass" data-testid="expedition-apply-btn">Apply for a Consultation <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </>
  );
}
